import {
  faDownload,
  faEye,
  faPencilAlt,
  faPrint
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import DatePickerComponent from 'components/common/DatePickerComponent';
import EmptyList from 'components/common/EmptyList';
import Flex from 'components/common/Flex';
import SearchInputWithSuggestions from 'components/common/SearchInputWithSuggestions';
import Pagination from 'components/pagination';
import { formatDateUs, useDebounce } from 'helpers/utils';
import useAdvanceTable from 'hooks/useAdvanceTable';
import { useFormatDateTime } from 'hooks/useFormatDateTime';
import useNotification from 'hooks/useNotification';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from 'react';
import { Badge, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import paths from 'routes/paths';
import CustomersService from 'services/Customers.service';
import InvoiceService from 'services/Invoice.service';
import InvoiceViewModal from '../modals/InvoiceViewModal';

const InvoiceList = forwardRef((props, ref) => {
  const navigate = useNavigate();
  const { showErrorNotification, showSuccessNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [invoices, setInvoices] = useState({
    content: [],
    totalElements: 0,
    totalPages: 0,
    size: 20,
    number: 0,
    totalElementsPerPage: 20,
    hasNextPage: false
  });

  const [filters, setFilters] = useState({
    page: 0,
    size: 20,
    status: '',
    customerId: '',
    startDate: '',
    endDate: ''
  });

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [customers, setCustomers] = useState([]);
  const [showCustomersSuggestions, setShowCustomersSuggestions] =
    useState(false);
  const [searchTermCustomer, setSearchTermCustomer] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExportingBulk, setIsExportingBulk] = useState(false);
  const [exportFileType, setExportFileType] = useState('pdf');
  const [exportType, setExportType] = useState('summary');

  const getData = useCallback(async () => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: filters.page,
        size: filters.size,
        ...(filters.status && { status: filters.status }),
        ...(filters.customerId && { customerId: filters.customerId }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate })
      }).toString();
      const response = await InvoiceService.list(queryParams);
      if (response && response.content) {
        setInvoices({
          ...response,
          totalElementsPerPage: response.size || 20,
          hasNextPage: response.number < response.totalPages - 1
        });
      }
    } catch (error) {
      console.error('Erro ao buscar faturas:', error);
      showErrorNotification('Erro ao carregar faturas');
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      startDate: startDate ? formatDateUs(startDate) : '',
      endDate: endDate ? formatDateUs(endDate) : '',
      page: 0
    }));
  }, [startDate, endDate]);

  useImperativeHandle(
    ref,
    () => ({
      refetch: () => {
        getData();
      }
    }),
    [getData]
  );

  const searchCustomers = async searchTerm => {
    if (!searchTerm || searchTerm.length < 2) {
      setCustomers([]);
      setShowCustomersSuggestions(false);
      return;
    }

    try {
      const queryParams = new URLSearchParams({
        page: 0,
        size: 20,
        includeInactives: false,
        name: searchTerm
      }).toString();
      const response = await CustomersService.list(queryParams);
      if (response.content) {
        setCustomers(response.content);
        setShowCustomersSuggestions(true);
      }
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
    }
  };

  const debouncedCustomerSearch = useDebounce(searchTerm => {
    searchCustomers(searchTerm);
  }, 400);

  useEffect(() => {
    if (searchTermCustomer.length >= 2) {
      debouncedCustomerSearch(searchTermCustomer);
    } else {
      setCustomers([]);
      setShowCustomersSuggestions(false);
    }
  }, [searchTermCustomer]);

  const handleCustomerSelect = customer => {
    setSelectedCustomer(customer);
    setSearchTermCustomer(customer.name || customer.fantasyName);
    setShowCustomersSuggestions(false);
    setFilters(prev => ({ ...prev, customerId: customer.id, page: 0 }));
  };

  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    setSearchTermCustomer('');
    setFilters(prev => ({ ...prev, customerId: '', page: 0 }));
  };

  const formatCurrency = value => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const getInvoiceStatusBadge = status => {
    const variants = {
      DRAFT: 'secondary',
      APPROVED: 'info',
      COMPLETED: 'success',
      CANCELED: 'danger'
    };
    const labels = {
      DRAFT: 'Rascunho',
      APPROVED: 'Aprovada',
      COMPLETED: 'Concluída',
      CANCELED: 'Cancelada'
    };
    return (
      <Badge bg={variants[status] || 'secondary'} className="fs-11">
        {labels[status] || status}
      </Badge>
    );
  };

  const handleViewInvoice = invoiceId => {
    setSelectedInvoiceId(invoiceId);
    setShowViewModal(true);
  };

  const handleEditInvoice = (code, id) => {
    const invoiceCode = code || id;
    navigate(
      paths.billingDetail.replace(':code', encodeURIComponent(invoiceCode)),
      { state: { invoiceId: id } }
    );
  };

  const handleExportPdf = async (invoiceId, invoiceCode) => {
    setIsExporting(true);
    try {
      const response = await InvoiceService.exportPdf(invoiceId);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `fatura-${invoiceCode || invoiceId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showSuccessNotification('PDF exportado com sucesso');
      } else {
        showErrorNotification('Erro ao exportar PDF');
      }
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      showErrorNotification('Erro ao exportar PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const handleBulkExport = async () => {
    if (selectedInvoices.length === 0) {
      showErrorNotification('Selecione pelo menos uma fatura para exportar');
      return;
    }

    setIsExportingBulk(true);
    try {
      const response = await InvoiceService.export(
        selectedInvoices,
        exportFileType,
        exportType
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        const fileExtension = exportFileType === 'excel' ? 'xlsx' : 'pdf';
        const fileName = `faturas-${exportType}-${Date.now()}.${fileExtension}`;
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showSuccessNotification(
          `${selectedInvoices.length} fatura(s) exportada(s) com sucesso`
        );
        setShowExportModal(false);
        table.setRowSelection({});
      } else {
        showErrorNotification('Erro ao exportar faturas');
      }
    } catch (error) {
      console.error('Erro ao exportar faturas:', error);
      showErrorNotification('Erro ao exportar faturas');
    } finally {
      setIsExportingBulk(false);
    }
  };

  const columns = [
    {
      accessorKey: 'code',
      header: 'Código',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { code, id } = rowData.row.original;
        return <div className="text-primary">{code || `#${id}`}</div>;
      }
    },
    {
      accessorKey: 'customer.name',
      header: 'Cliente',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { customer } = rowData.row.original;
        return <div>{customer.name || '-'}</div>;
      }
    },

    {
      accessorKey: 'totalValue',
      header: 'Valor Total',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { totalValue } = rowData.row.original;
        return <div className="fw-bold">{formatCurrency(totalValue)}</div>;
      }
    },
    {
      accessorKey: 'creationDate',
      header: 'Data de Criação',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { creationDate } = rowData.row.original;
        const formatDateTime = useFormatDateTime();
        return <div>{formatDateTime(creationDate)}</div>;
      }
    },
    {
      accessorKey: 'status',
      header: 'Status',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { status } = rowData.row.original;
        return getInvoiceStatusBadge(status);
      }
    },
    {
      accessorKey: 'actions',
      header: 'Ações',
      meta: {
        headerProps: { className: 'text-900 text-center' },
        cellProps: { className: 'text-center' }
      },
      cell: rowData => {
        const { code, id, status } = rowData.row.original;
        return (
          <Flex justifyContent="center" alignItems="center" className="gap-2">
            <Button
              variant="link"
              size="sm"
              className="p-1"
              onClick={e => {
                e.stopPropagation();
                handleViewInvoice(id);
              }}
              title="Visualizar"
            >
              <FontAwesomeIcon icon={faEye} />
            </Button>
            <Button
              variant="link"
              size="sm"
              className="p-1"
              onClick={e => {
                e.stopPropagation();
                handleEditInvoice(code, id);
              }}
              title="Editar"
              disabled={status === 'COMPLETED' || status === 'CANCELED'}
            >
              <FontAwesomeIcon icon={faPencilAlt} />
            </Button>
            <Button
              variant="link"
              size="sm"
              className="p-1"
              onClick={e => {
                e.stopPropagation();
                handleExportPdf(id, code);
              }}
              disabled={isExporting}
              title="Imprimir PDF"
            >
              <FontAwesomeIcon icon={faPrint} />
            </Button>
          </Flex>
        );
      }
    }
  ];

  const tableData = useMemo(() => {
    return invoices.content;
  }, [invoices.content]);

  const table = useAdvanceTable({
    data: tableData,
    columns,
    selection: true,
    selectionColumnWidth: 50
  });

  const selectedInvoices = useMemo(() => {
    const selectedRows = table.getSelectedRowModel().rows;
    return selectedRows.map(row => row.original.id);
  }, [table.getState().rowSelection]);

  const isEmpty = !isLoading && invoices.content.length === 0;

  return (
    <>
      <div className="mb-3">
        <h6 className="mb-3">Filtros</h6>
        <Row className="g-3">
          <Col md={3}>
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={filters.status}
              onChange={e =>
                setFilters(prev => ({
                  ...prev,
                  status: e.target.value,
                  page: 0
                }))
              }
            >
              <option value="">Todos</option>
              <option value="DRAFT">Rascunho</option>
              <option value="APPROVED">Aprovada</option>
              <option value="COMPLETED">Concluída</option>
              <option value="CANCELED">Cancelada</option>
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Label>Cliente</Form.Label>
            <div className="position-relative">
              <SearchInputWithSuggestions
                searchTerm={searchTermCustomer}
                placeholder="Digite para buscar cliente..."
                suggestions={customers}
                showSuggestions={showCustomersSuggestions}
                onSearchChange={e => {
                  setSearchTermCustomer(e.target.value);
                  setShowCustomersSuggestions(e.target.value.length > 0);
                }}
                onSuggestionSelect={handleCustomerSelect}
                renderSuggestionItem={customer => (
                  <div
                    key={customer.id}
                    className="px-3 py-2 border-bottom cursor-pointer hover-bg-200"
                    onClick={() => handleCustomerSelect(customer)}
                    role="button"
                  >
                    <div className="fw-bold">
                      {customer.type === 'JURIDICAL_PERSON'
                        ? customer.fantasyName
                        : customer.name}
                    </div>
                    <div className="text-muted small">
                      {customer.cpf || customer.cnpj}
                    </div>
                  </div>
                )}
              />
              {selectedCustomer && (
                <button
                  type="button"
                  className="btn btn-link btn-sm position-absolute end-0 top-50 translate-middle-y me-2"
                  onClick={handleClearCustomer}
                >
                  ×
                </button>
              )}
            </div>
          </Col>
          <Col md={6}>
            <Form.Label>Período</Form.Label>
            <DatePickerComponent
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              applyCustomStyles={false}
            />
          </Col>
        </Row>
      </div>

      <div>
        <Flex justifyContent="between" alignItems="center" className="mb-3">
          <h6 className="mb-0">Faturas</h6>
          {selectedInvoices.length > 0 && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowExportModal(true)}
            >
              <FontAwesomeIcon icon={faDownload} className="me-2" />
              Exportar Selecionadas ({selectedInvoices.length})
            </Button>
          )}
        </Flex>
        <div>
          {isLoading ? (
            <Flex justifyContent="center" className="py-4">
              <Spinner animation="border" variant="primary" />
            </Flex>
          ) : isEmpty ? (
            <EmptyList imgSize={500} vhSize={75} />
          ) : (
            <>
              <AdvanceTableProvider {...table}>
                <AdvanceTable
                  headerClassName="text-nowrap align-middle"
                  rowClassName="align-middle white-space-nowrap"
                  tableProps={{
                    striped: true,
                    className: 'fs-10 mb-0 overflow-hidden'
                  }}
                />
              </AdvanceTableProvider>
              <Pagination
                data={invoices}
                filters={filters}
                setFilters={setFilters}
              />
            </>
          )}
        </div>
      </div>
      <InvoiceViewModal
        show={showViewModal}
        onHide={() => {
          setShowViewModal(false);
          setSelectedInvoiceId(null);
        }}
        invoiceId={selectedInvoiceId}
        onStatusUpdated={() => {
          getData();
        }}
      />

      <Modal show={showExportModal} onHide={() => setShowExportModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Exportar Faturas</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            Você está prestes a exportar{' '}
            <strong>{selectedInvoices.length}</strong> fatura(s) selecionada(s).
          </p>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Tipo de Arquivo</Form.Label>
              <Form.Select
                value={exportFileType}
                onChange={e => setExportFileType(e.target.value)}
              >
                <option value="pdf">PDF</option>
                <option value="excel">Excel (XLSX)</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Tipo de Exportação</Form.Label>
              <Form.Select
                value={exportType}
                onChange={e => setExportType(e.target.value)}
              >
                <option value="summary">Resumo</option>
                <option value="detailed">Detalhado</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="falcon-default"
            onClick={() => setShowExportModal(false)}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleBulkExport}
            disabled={isExportingBulk}
          >
            {isExportingBulk ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Exportando...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                Exportar
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
});

InvoiceList.displayName = 'InvoiceList';

export default InvoiceList;
